import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function TestHistoryPage() {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 'all', 'recruitment', 'practice'
    const navigate = useNavigate();
    
    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    // 1. Fetch Data
    useEffect(() => {
        const fetchHistory = async () => {
            const token = localStorage.getItem('skillmatch_token');
            if (!token) return navigate('/login');

            try {
                const res = await fetch(`${API_URL}/api/users/test-history`, {
                    headers: { 'x-auth-token': token }
                });
                if (res.ok) {
                    const data = await res.json();
                    setHistory(data);
                }
            } catch (error) {
                console.error("Lỗi tải lịch sử:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, [navigate, API_URL]);

    // 2. Logic Filter
    const filteredHistory = history.filter(item => {
        if (filter === 'all') return true;
        return item.type === filter;
    });

    // 3. Xử lý click -> Chuyển sang trang kết quả
    const handleViewResult = (item) => {
        // Truyền state giống hệt cấu trúc TestResultPage mong đợi
        navigate(`/candidate/test-result`, {
            state: {
                score: item.score,
                totalQuestions: item.totalQuestions,
                correctCount: item.correctCount,
                violationCount: item.violationCount,
                isTerminated: item.isTerminated,
                skillAnalysis: item.skillAnalysis,
                aiFeedback: item.aiFeedback,
                aiSuggestion: item.aiSuggestion
            }
        });
    };

    // Helpers
    const formatDate = (dateStr) => {
        if(!dateStr) return '---';
        return new Date(dateStr).toLocaleDateString('vi-VN', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute:'2-digit'
        });
    };

    const formatDuration = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}p ${s}s`;
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-green-600 bg-green-50 border-green-100';
        if (score >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-100';
        return 'text-red-600 bg-red-50 border-red-100';
    };

    if (loading) return (
        <div className="min-h-screen pt-24 flex items-center justify-center bg-[#F8FAFC]">
            <span className="material-symbols-outlined animate-spin text-3xl text-blue-600">sync</span>
        </div>
    );

    return (
        <div className="bg-[#F8FAFC] min-h-screen pt-28 pb-12 px-6 font-sans">
            <div className="max-w-6xl mx-auto">
                
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4 animate-fade-up">
                    <div>
                        <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600">
                            Lịch sử làm bài
                        </h1>
                        <p className="text-gray-500 mt-2 font-medium">Xem lại kết quả các bài kiểm tra năng lực của bạn.</p>
                    </div>
                    
                    {/* Filter Tabs */}
                    <div className="bg-white p-1 rounded-xl border border-gray-200 shadow-sm flex">
                        {[
                            { id: 'all', label: 'Tất cả' },
                            { id: 'recruitment', label: 'Tuyển dụng' },
                            { id: 'practice', label: 'Luyện tập' }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setFilter(tab.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                                    filter === tab.id 
                                    ? 'bg-blue-50 text-blue-600 shadow-sm' 
                                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* List Content */}
                {filteredHistory.length === 0 ? (
                    <div className="bg-white rounded-3xl p-16 text-center shadow-lg border border-gray-100 animate-fade-up">
                        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-5xl text-gray-300">history_edu</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Chưa có bài làm nào</h3>
                        <p className="text-gray-500 mt-2 mb-8">Bạn chưa thực hiện bài kiểm tra nào thuộc danh mục này.</p>
                        <button onClick={() => navigate('/tests')} className="btn-signature px-8 py-3 text-sm shadow-blue-500/20">Làm bài test ngay</button>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {filteredHistory.map((item, idx) => (
                            <div 
                                key={item._id}
                                onClick={() => handleViewResult(item)}
                                className="group bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col md:flex-row items-center gap-6 animate-fade-up"
                                style={{ animationDelay: `${idx * 0.05}s` }}
                            >
                                {/* Icon Type */}
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-inner ${
                                    item.type === 'recruitment' 
                                    ? 'bg-indigo-50 text-indigo-600' 
                                    : 'bg-teal-50 text-teal-600'
                                }`}>
                                    <span className="material-symbols-outlined">
                                        {item.type === 'recruitment' ? 'business_center' : 'school'}
                                    </span>
                                </div>

                                {/* Info Main */}
                                <div className="flex-1 w-full text-center md:text-left">
                                    <div className="flex items-center justify-center md:justify-start gap-3 mb-1">
                                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                                            {item.testName}
                                        </h3>
                                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${
                                            item.type === 'recruitment' 
                                            ? 'bg-indigo-50 text-indigo-700 border-indigo-100' 
                                            : 'bg-teal-50 text-teal-700 border-teal-100'
                                        }`}>
                                            {item.type === 'recruitment' ? 'Công ty' : 'Practice'}
                                        </span>
                                    </div>
                                    
                                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-gray-500 mt-2">
                                        <span className="flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[16px]">calendar_today</span>
                                            {formatDate(item.submittedAt)}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[16px]">timer</span>
                                            {formatDuration(item.duration)}
                                        </span>
                                        {item.isTerminated && (
                                            <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-0.5 rounded text-xs font-bold">
                                                <span className="material-symbols-outlined text-[14px]">warning</span> Bị hủy bài
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Score & Action */}
                                <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 border-gray-50 pt-4 md:pt-0">
                                    <div className="text-right">
                                        <div className={`text-2xl font-black px-3 py-1 rounded-lg border ${getScoreColor(item.score)}`}>
                                            {item.score}<span className="text-sm font-normal opacity-70">/100</span>
                                        </div>
                                        <div className="text-xs text-gray-400 mt-1 font-medium text-center">Điểm số</div>
                                    </div>
                                    
                                    <button className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-blue-600 group-hover:text-white transition-all">
                                        <span className="material-symbols-outlined">arrow_forward_ios</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}